import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { log } from 'src/app/utils/logging';
import { actionUpdateJob } from 'src/app/v1/state/modules/session/job';
import { actionUpdateJobOrderId } from 'src/app/v1/state/modules/session/jobOrderId';
import { actionUpdateSeekListingId } from 'src/app/v1/state/modules/tealium/seekListingId';
import {
  type CreateProductSetForAdRefreshMutation,
  type OrderRequestInputItem,
  ProductTypeInput,
  useCreateOrderWithPromotionMutation,
  useCreateProductSetForAdRefreshMutation,
  useRefreshJobMutation,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

import { useMutationWithAbort } from '../useMutationWithAbort';

type ProductSet =
  CreateProductSetForAdRefreshMutation['createProductSetForAdRefresh']['productSet'];

export const useCreateRefreshedJobAndOrder = () => {
  const dispatch = useDispatch();

  const { advertiserNation, sourceJobId, seekAdvertiserId } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      sourceJobId: state.session.sourceJobId,
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );

  const [createRefreshedJob] = useRefreshJobMutation({
    context: {
      headers: {
        site: advertiserNation,
      },
    },
    onError: (error) => {
      log.error('useCreateRefreshedJobAndOrder | RefreshJob', {
        error,
        advertiserNation,
        sourceJobId,
        seekAdvertiserId,
      });
    },
  });

  const [createOrder] = useCreateOrderWithPromotionMutation({
    context: {
      headers: {
        site: advertiserNation,
      },
    },
    onError: (error) => {
      log.error('useCreateRefreshedJobAndOrder | CreateOrder', {
        error,
        advertiserNation,
        sourceJobId,
        seekAdvertiserId,
      });
    },
  });

  const idempotencyKey = uuidv4();

  const [createProductSetForAdRefreshMutation] =
    useCreateProductSetForAdRefreshMutation();

  const createProductSetForAdRefreshMutationWithAbort = useMutationWithAbort(
    createProductSetForAdRefreshMutation,
  );

  const createRefreshedJobAndOrder = async () => {
    if (!(seekAdvertiserId && sourceJobId)) {
      return null;
    }

    const { data: jobData } = await createRefreshedJob({
      variables: {
        input: {
          advertiserId: seekAdvertiserId,
          jobId: sourceJobId,
          idempotencyKey,
        },
      },
    });

    if (!jobData?.refreshJob) {
      return null;
    }

    const { data: productSetData } =
      await createProductSetForAdRefreshMutationWithAbort({
        variables: {
          input: {
            jobId: sourceJobId,
            draftId: uuidv4(),
          },
        },
        onError: (error) => {
          log.error(
            'useCreateRefreshedJobAndOrder | CreateProductSetForAdRefresh',
            {
              error,
              advertiserNation,
              sourceJobId,
              seekAdvertiserId,
            },
          );
        },
      });

    const productSet = productSetData?.createProductSetForAdRefresh?.productSet;

    if (!productSet?.items.length) {
      return null;
    }

    const orderLineItems: OrderRequestInputItem[] = productSet.items.map(
      (each: ProductSet) => ({
        productType: ProductTypeInput.CATALOGUE_PRODUCT,
        productAttributes: {
          catalogueProduct: {
            jobId: jobData.refreshJob?.jobId,
            revisionId: jobData.refreshJob?.revisionId,
            productSetId: productSet.id,
            productId: each.id,
          },
        },
      }),
    );

    const { data: orderData } = await createOrder({
      variables: {
        input: {
          advertiserId: seekAdvertiserId,
          advertiserNation,
          lineItems: orderLineItems,
          orderCreationId: uuidv4(),
        },
      },
    });

    if (
      orderData?.createOrderWithPromotion?.__typename !==
      'CreateOrderSuccessPayload'
    ) {
      return null;
    }

    return {
      orderId: orderData?.createOrderWithPromotion?.order?.orderId,
      jobId: jobData.refreshJob.jobId,
      revisionId: jobData.refreshJob.revisionId,
    };
  };

  return async () => {
    const response = await createRefreshedJobAndOrder();
    if (response?.orderId) {
      dispatch(
        actionUpdateJob({
          id: response.jobId,
          idempotencyKey,
          revisionId: response.revisionId,
        }),
      );
      dispatch(actionUpdateSeekListingId(response.jobId));
      dispatch(actionUpdateJobOrderId(response.orderId));
    }

    return response;
  };
};
